import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Bot, User } from "lucide-react";
import { API } from "@/lib/api";

const QUICK = [
  "What fees do I pay when buying in Dubai?",
  "Can foreigners buy freehold property?",
  "How does the Golden Visa work with property?",
];

const GREETING = {
  role: "assistant",
  text: "Hello — I'm Haven, your Dubai property concierge. Ask me anything about buying here: the process, DLD fees, freehold areas, escrow rules or the Golden Visa.",
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(
    localStorage.getItem("hh-chat-session") ||
      (() => {
        const id = `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem("hh-chat-session", id);
        return id;
      })()
  ).current;

  useEffect(() => {
    if (!open || loaded) return;
    fetch(`${API}/chat/history/${sessionId}`)
      .then((r) => r.json())
      .then((rows) => {
        if (rows.length) setMessages([GREETING, ...rows.map((m) => ({ role: m.role, text: m.text }))]);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [open, loaded, sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ") || line === "data: [DONE]") continue;
          try {
            const { delta } = JSON.parse(line.slice(6));
            if (delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Connection hiccup — please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-open-btn"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gold text-ink flex items-center justify-center shadow-[0_8px_30px_rgba(212,175,55,0.35)] hover:bg-gold-hover transition-colors"
        aria-label="Open AI concierge"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid="chat-panel"
            className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[400px] h-[560px] max-h-[70vh] glass border border-gold/20 rounded-3xl overflow-hidden flex flex-col shadow-2xl"
          >
            <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3 bg-ink-card/80">
              <span className="w-9 h-9 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center">
                <Bot size={17} className="text-gold" />
              </span>
              <div>
                <p className="font-serif text-base text-[#F8F6F0]">Haven — Property Concierge</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">RERA &amp; DLD-informed guidance</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto chat-scroll px-4 py-4 space-y-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : ""}`}>
                  {m.role === "assistant" && (
                    <span className="shrink-0 w-7 h-7 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center mt-0.5">
                      <Bot size={13} className="text-gold" />
                    </span>
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-gold text-ink rounded-br-sm"
                        : "bg-white/5 text-zinc-200 border border-white/10 rounded-bl-sm"
                    }`}
                  >
                    {m.text || <span className="inline-block w-4 h-4 align-middle border-2 border-gold/40 border-t-gold rounded-full animate-spin" />}
                  </div>
                  {m.role === "user" && (
                    <span className="shrink-0 w-7 h-7 rounded-full bg-white/10 flex items-center justify-center mt-0.5">
                      <User size={13} className="text-zinc-300" />
                    </span>
                  )}
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {QUICK.map((q) => (
                    <button
                      key={q}
                      data-testid={`chat-quick-${q.slice(0, 12).replace(/\W/g, "-").toLowerCase()}`}
                      onClick={() => send(q)}
                      className="text-[11px] text-gold-pale border border-gold/25 rounded-full px-3 py-1.5 hover:bg-gold/10 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="px-4 pb-2 text-[10px] text-zinc-600 leading-snug" data-testid="chat-disclaimer">
              General guidance only — not legal or financial advice. Verify everything on the Dubai REST app / DLD.
            </div>
            <div className="p-3 border-t border-white/10 flex items-center gap-2 bg-ink-card/80">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about buying in Dubai…"
                className="flex-1 bg-ink border border-white/10 rounded-full px-4 py-2.5 text-sm text-[#F8F6F0] placeholder:text-zinc-600 focus:outline-none focus:border-gold/50"
              />
              <button
                data-testid="chat-send-btn"
                onClick={() => send()}
                disabled={busy || !input.trim()}
                className="w-10 h-10 rounded-full bg-gold text-ink flex items-center justify-center hover:bg-gold-hover disabled:opacity-50 transition-colors"
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
